import { useEffect, useCallback, useState, useMemo, useRef } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";
import Lottie from "react-lottie";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import {
    ConfluenceBackground,
    JiraBackground
} from "@atlassian/product-wireframes";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper,
    ufoEventOnboardingWizardProjectCreation,
    ufoEventOnboardingWizardProjectCreationIncludingProvisioning,
    getFirstName,
    getIdentityBaseUrl
} from "@/components/signup/utils";
import {
    useBxpWebStargateClient,
    WizardCreateProjectPayload
} from "@/libraries/bxp-web-stargate-client/src";
import { DwellTimeWrapper } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/components/dwell-time-wrapper";
import {
    useCloudStatus,
    useCloudStatusRedirection
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import { getProductQueryParamTeamType } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/models/user-segmentation";
import { trackPerceivedProvisioningEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics";
import messages from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/common/messages";
import { useWizardConfiguration } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/controllers";
import {
    ProjectDetails,
    type ProjectConfig
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/utils/types";
import { PROJECT_CONFIG_SESSION_STORAGE_KEY } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/onboarding-wizard/wizard/page-switcher/constants";
import { experiments, getExperimentWithExposures } from "@/utils/experiment";

import { useLoggedInUser } from "../../../../../../../../../utils";
import ProgressBar from "../../../sections/progress-bar";
import { getTopLevelDomain } from "../../../utils/create-product-url";
import { getGenericSampleVersion } from "../../setup/jira/utils";

import IconsAnimationLight from "./lotties/icons.light.json";
import EndProvisioningAnimation from "./lotties/provisioning-end-animation.json";
import ProvisioningAnimationLight from "./lotties/provisioning.light.json";
import { ModalOverlay } from "./modal-overlay";

const EndAnimationContainerStyle = css({
    width: 512
});

const EndAnimationParagraphStyle = css({
    margin: "16px",
    textAlign: "center",
    fontWeight: "400",
    lineHeight: "24px"
});

const modalContentStyle = css({
    display: "flex",
    flexFlow: "column",
    alignItems: "center",
    alignSelf: "center",
    position: "relative",
    zIndex: 1
});

const backgroundAnimationStyle = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        position: "absolute",
        insetBlockStart: 0,
        insetInlineStart: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        borderRadius: "4px",
        pointerEvents: "none",
        overflow: "hidden"
    }
});

const iconAnimationStyle = css({
    maxWidth: "100%",
    width: "400px"
});

const videoAnimationStyle = css({
    maxWidth: "100%",
    width: "100%",
    height: "auto",
    display: "block"
});

// DAM CDN video URL for TWC co-use animation (Variant 1 / "video")
const TWC_VALUE_VIDEO_URL =
    "https://dam-cdn.atl.orangelogic.com/AssetLink/c7in888307ebr015f3w2ik737060v83a.webm";

const EndAnimationHeaderStyle = css({
    textAlign: "center",
    fontSize: "24px",
    fontWeight: 653,
    lineHeight: "28px",
    marginBlockStart: 160,
    marginBlockEnd: 0
});

const VideoHeaderStyle = css({
    textAlign: "center",
    fontSize: "24px",
    fontWeight: 653,
    lineHeight: "28px",
    marginBlockEnd: 0
});

export const ControlAnimation = ({
    provisioningCompleteDefault = false
}: {
    provisioningCompleteDefault?: boolean;
    // eslint-disable-next-line complexity
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();
    const [provisioningComplete, setProvisioningComplete] = useState(
        provisioningCompleteDefault
    );
    const userInfo = useLoggedInUser();
    const name = getFirstName(userInfo.name);

    // TWCG-1856 experiment: "video" = Variant 1 (new animation + copy), "welcome" = Variant 2 (welcome screen copy)
    const provisioningValueExperiment = getExperimentWithExposures(
        experiments.twcg1856ProvisioningValueUpgradeLand
    );
    const experimentCohort =
        provisioningValueExperiment.type === "enrolled" &&
        !provisioningValueExperiment.usingDefault
            ? provisioningValueExperiment.value
            : "control";
    const isVideoVariant = experimentCohort === "video";
    const isWelcomeVariant = experimentCohort === "welcome";

    const {
        isProduction,
        onDemandUrl,
        createCloudStatus,
        cloudId,
        perceivedProvisioningStartTime,
        productActivationStatusResponses
    } = useCloudStatus();

    const {
        redirectUrl,
        deferRedirectView,
        setDeferRedirectView,
        resolveBlockingRedirectPromises
    } = useCloudStatusRedirection();

    const {
        products,
        signupId,
        jtbdD0Onboarding,
        jtbdD0OnboardingTemplateTitle
    } = useSearchParameters();
    const productKey = products.length > 0 ? products[0] : undefined;
    const isConfluenceProduct = jtbdD0Onboarding && jtbdD0Onboarding !== "jira";
    const isJiraProduct = jtbdD0Onboarding && jtbdD0Onboarding === "jira";
    const Background = isConfluenceProduct
        ? ConfluenceBackground
        : JiraBackground;

    // Jira project creation hooks and state
    const stargateClient = useBxpWebStargateClient();
    const { teamType, wizardConfiguration } = useWizardConfiguration();
    const isCreateRequestInProgress = useRef(false);
    const [hasCreatedProject, setHasCreatedProject] = useState(false);
    const [jtbdJiraOnboardingUrl, setJtbdJiraOnboardingUrl] = useState<
        string | undefined
    >(undefined);
    const projectDetails: ProjectDetails = {
        projectKey: "KAN",
        template: "KANBAN",
        defaultProjectName: messages.defaultProjectName
    };

    const jtbdConfluenceOnboardingUrl = useMemo(() => {
        if (
            !onDemandUrl ||
            !jtbdD0Onboarding ||
            jtbdD0Onboarding === "" ||
            !jtbdD0OnboardingTemplateTitle ||
            jtbdD0OnboardingTemplateTitle === ""
        ) {
            return undefined;
        }

        const instanceUrl =
            "https://" +
            encodeURIComponent(String(onDemandUrl)) +
            getTopLevelDomain(isProduction);

        return `${instanceUrl}/wiki/create-content/page?title=${jtbdD0OnboardingTemplateTitle}&withFallback=true&source=contentTypeOnboarding&templateKey=${jtbdD0Onboarding}&onboarding=twc`;
    }, [
        onDemandUrl,
        jtbdD0Onboarding,
        jtbdD0OnboardingTemplateTitle,
        isProduction
    ]);

    const listViewUrl = useMemo(() => {
        if (!onDemandUrl || !isJiraProduct) {
            return undefined;
        }

        const instanceUrl =
            "https://" +
            encodeURIComponent(String(onDemandUrl)) +
            getTopLevelDomain(isProduction);

        return `${instanceUrl}/jira/software/projects/${projectDetails.projectKey}/list`;
    }, [onDemandUrl, isJiraProduct, isProduction, projectDetails.projectKey]);

    const redirectCallback = useCallback(
        async (url: string) => {
            createAnalyticsEvent({
                eventType: "ui",
                action: "succeeded",
                actionSubject: "provisioning",
                actionSubjectId: "twcProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    experimentVariant: experimentCohort
                }
            }).fire();

            createAnalyticsEvent({
                eventName: "redirectedToProduct",
                eventComponent: "confirmationPage",
                event: "redirected",
                eventType: "ui",
                action: "redirected"
            }).fire();

            trackPerceivedProvisioningEvent({
                createAnalyticsEvent,
                productKey,
                finalProvisioningScreen: "twc-provisioning-screen",
                startTimeSeconds: perceivedProvisioningStartTime,
                signupId
            });

            // SLO "good event" for redirect into product
            ufoEventRedirectToProduct.success({
                customData: {
                    reactComponent: "TwcProvisioningScreen",
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    finalProvisioningScreen: "twc-provisioning-screen"
                }
            });

            await resolveBlockingRedirectPromises();

            window.location.assign(url);
        },
        [
            createAnalyticsEvent,
            perceivedProvisioningStartTime,
            productKey,
            signupId,
            resolveBlockingRedirectPromises,
            experimentCohort
        ]
    );

    const projectConfig = useMemo((): ProjectConfig => {
        // Compute default config as fallback
        const defaultIssueTypes = wizardConfiguration.issueTypes;
        const defaultColumns = wizardConfiguration.columns.map(
            (columnMessage) =>
                formatMessage({
                    id: columnMessage.id,
                    defaultMessage: columnMessage.defaultMessage
                })
        );

        const selectedIssueTypes = defaultIssueTypes.filter(
            (issue) => issue.isPreSelected
        );

        const defaultProjectConfig = {
            projectName: formatMessage(projectDetails.defaultProjectName),
            issueTypes: selectedIssueTypes.map(({ icon, avatarId }) => ({
                name: icon,
                avatarId
            })),
            columns: defaultColumns
        };

        const storedValue = sessionStorage.getItem(
            PROJECT_CONFIG_SESSION_STORAGE_KEY
        );

        if (storedValue) {
            try {
                return JSON.parse(storedValue) as ProjectConfig;
            } catch {
                return defaultProjectConfig;
            }
        }

        return defaultProjectConfig;
    }, [wizardConfiguration, projectDetails.defaultProjectName, formatMessage]);

    const createJiraProject = useCallback(async () => {
        if (!cloudId || !isJiraProduct || hasCreatedProject || !projectConfig) {
            return;
        }

        ufoEventOnboardingWizardProjectCreation.start();
        ufoEventOnboardingWizardProjectCreation.setCustomData({ signupId });

        ufoEventOnboardingWizardProjectCreationIncludingProvisioning.startSubstep(
            UfoCustomTimingKey.ONBOARDING_WIZARD_PROJECT_CREATE_API_CALL
        );

        try {
            isCreateRequestInProgress.current = true;

            const projectPayload: WizardCreateProjectPayload = {
                projectName: projectConfig.projectName,
                issueTypes: projectConfig.issueTypes,
                statusNames: projectConfig.columns,
                projectKey: projectDetails.projectKey,
                template: projectDetails.template,
                cloudId: cloudId,
                teamType: getProductQueryParamTeamType(teamType),
                customDetail: {
                    enableExtraFields: true,
                    sampleDataVersion: getGenericSampleVersion(
                        teamType,
                        "KANBAN"
                    )
                }
            };

            const { taskId } = await stargateClient.wizardProjectCreate(
                projectPayload
            );

            // Poll for project creation to complete
            const { status } = await stargateClient.pollTaskUntilComplete(
                taskId,
                cloudId
            );

            if (status === "COMPLETE") {
                setHasCreatedProject(true);
                setJtbdJiraOnboardingUrl(listViewUrl);

                ufoEventOnboardingWizardProjectCreation.success({
                    customData: {
                        template: projectDetails.template
                    }
                });

                createAnalyticsEvent({
                    eventType: "track",
                    action: "succeeded",
                    actionSubject: "jiraProjectCreation",
                    actionSubjectId: "twcProvisioningScreen",
                    source: "/ondemand/signup/confirmation",
                    attributes: {
                        template: projectDetails.template,
                        teamType: teamType
                    }
                }).fire();
            }
        } catch (error) {
            ufoEventOnboardingWizardProjectCreation.failure({
                customData: {
                    template: projectDetails.template
                },
                error
            });

            createAnalyticsEvent({
                eventType: "track",
                action: "failed",
                actionSubject: "jiraProjectCreation",
                actionSubjectId: "twcProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    template: projectDetails.template,
                    teamType: teamType,
                    error:
                        error instanceof Error ? error.message : "Unknown error"
                }
            }).fire();

            // Still mark as created to prevent retries
            setHasCreatedProject(true);
            setJtbdJiraOnboardingUrl(redirectUrl);
        } finally {
            isCreateRequestInProgress.current = false;
        }
    }, [
        cloudId,
        isJiraProduct,
        hasCreatedProject,
        signupId,
        projectConfig,
        projectDetails.projectKey,
        projectDetails.template,
        teamType,
        stargateClient,
        createAnalyticsEvent,
        listViewUrl,
        redirectUrl
    ]);

    const getOnboardingUrl = useCallback(() => {
        if (isConfluenceProduct) {
            return jtbdConfluenceOnboardingUrl || redirectUrl;
        }

        if (isJiraProduct && jtbdJiraOnboardingUrl) {
            return jtbdJiraOnboardingUrl;
        }

        const twcOnboardingStatusResponse =
            productActivationStatusResponses?.[0];
        const twcOnboardingStatus =
            twcOnboardingStatusResponse?.twcOnboardingStatus;

        if (
            twcOnboardingStatus?.status === "completed" &&
            twcOnboardingStatus.onboardingUrl
        ) {
            return twcOnboardingStatus.onboardingUrl;
        }

        if (twcOnboardingStatus?.status === "failed") {
            createAnalyticsEvent({
                eventType: "ui",
                action: "failed",
                actionSubject: "TwcOnboardingStatus Failed",
                actionSubjectId: "twcProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    reason: "TWC onboarding status response was failed and no onboarding URL was provided"
                }
            }).fire();
        }

        return redirectUrl;
    }, [
        isConfluenceProduct,
        jtbdConfluenceOnboardingUrl,
        isJiraProduct,
        jtbdJiraOnboardingUrl,
        productActivationStatusResponses,
        createAnalyticsEvent,
        redirectUrl
    ]);

    useEffect(() => {
        if (
            createCloudStatus === "success" &&
            productActivationStatusResponses &&
            productActivationStatusResponses.length > 0
        ) {
            // For jtbd d0 onboarding experiment and jira redirect, wait for project creation to complete before redirecting
            if (isJiraProduct && !hasCreatedProject) {
                return;
            }

            setProvisioningComplete(true);

            setTimeout(() => {
                const url = `${getIdentityBaseUrl()}/login?${new URLSearchParams(
                    {
                        continue: getOnboardingUrl(),
                        application: "wac--teamwork-collection"
                    }
                )}`;

                redirectCallback(url);
            }, 3500); // give time for final animation to play
        }
    }, [
        createCloudStatus,
        redirectCallback,
        productActivationStatusResponses,
        isJiraProduct,
        hasCreatedProject,
        getOnboardingUrl
    ]);

    // Trigger Jira project creation when provisioning completes
    useEffect(() => {
        if (
            projectConfig &&
            createCloudStatus === "success" &&
            cloudId &&
            isJiraProduct &&
            !hasCreatedProject &&
            !isCreateRequestInProgress.current
        ) {
            createJiraProject();
        }
    }, [
        projectConfig,
        createCloudStatus,
        cloudId,
        isJiraProduct,
        hasCreatedProject,
        createJiraProject
    ]);

    // Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
    useEffect(() => {
        if (!deferRedirectView) {
            if (setDeferRedirectView) {
                setDeferRedirectView("twc-provisioning-screen");
            }
        }
    });

    // Initial analytics events — fire on mount
    useEffect(() => {
        createAnalyticsEvent({
            eventType: "screen",
            name: "twcProvisioningScreen",
            attributes: {
                experimentVariant: experimentCohort
            }
        }).fire();
    }, [createAnalyticsEvent, experimentCohort]);

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={UfoCustomTimingKey.TWC_PROVISIONING_DWELL_TIME}
        >
            <DwellTimeWrapper
                eventData={{
                    action: "dwelled",
                    actionSubject: "screen",
                    actionSubjectId: "twcProvisioningScreen",
                    source: "/ondemand/signup/confirmation"
                }}
                initialPollInterval={5000}
                idleTimeout={120000}
                nextPollIntervalCallback={(x) => x + 5000}
            >
                <Background>
                    <ModalOverlay>
                        <div css={backgroundAnimationStyle}>
                            {!provisioningComplete ? (
                                !isVideoVariant ? (
                                    <Lottie
                                        options={{
                                            loop: false,
                                            animationData:
                                                ProvisioningAnimationLight
                                        }}
                                    />
                                ) : null
                            ) : (
                                <Lottie
                                    options={{
                                        loop: false,
                                        animationData: EndProvisioningAnimation
                                    }}
                                />
                            )}
                        </div>
                        <div css={modalContentStyle}>
                            {!provisioningComplete ? (
                                <>
                                    {isVideoVariant ? (
                                        // Variant 1 ("video"): TWC co-use animation from DAM
                                        <video
                                            css={videoAnimationStyle}
                                            src={TWC_VALUE_VIDEO_URL}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            data-testid="twc-value-video-animation"
                                        />
                                    ) : (
                                        <div css={iconAnimationStyle}>
                                            <Lottie
                                                options={{
                                                    loop: true,
                                                    animationData:
                                                        IconsAnimationLight
                                                }}
                                            />
                                        </div>
                                    )}
                                    <ProgressBar
                                        isSuccessPreconditionsMet={
                                            !isJiraProduct || hasCreatedProject
                                        }
                                    />
                                    {isVideoVariant && (
                                        // Variant 1 ("video"): new provisioning headline
                                        <h3
                                            css={VideoHeaderStyle}
                                            data-testid="teamwork-collection-provisioning-headline"
                                        >
                                            <FormattedMessage
                                                id="WacConfirmationPage.teamworkCollectionProvisioning.video.headline"
                                                defaultMessage="Almost there! We're setting up your workspace."
                                            />
                                        </h3>
                                    )}
                                    <p
                                        css={EndAnimationParagraphStyle}
                                        data-testid="teamwork-collection-provisioning-message"
                                    >
                                        {isVideoVariant ? (
                                            // Variant 1 ("video"): new provisioning subheading
                                            <FormattedMessage
                                                id="WacConfirmationPage.teamworkCollectionProvisioning.video"
                                                defaultMessage="In just a moment, you'll have Jira, Confluence, Loom, and Rovo — all connected and ready to go."
                                            />
                                        ) : (
                                            // Control: existing provisioning copy
                                            <FormattedMessage
                                                id="WacConfirmationPage.teamworkCollectionProvisioning"
                                                defaultMessage="Getting your collection ready—this will only take a moment. You will be redirected shortly."
                                            />
                                        )}
                                    </p>
                                </>
                            ) : (
                                <div css={EndAnimationContainerStyle}>
                                    <h3 css={EndAnimationHeaderStyle}>
                                        {isWelcomeVariant ? (
                                            // Variant 2 ("welcome"): new welcome screen headline
                                            <FormattedMessage
                                                id="WacConfirmationPage.teamworkCollectionProvisioningComplete.welcome.video"
                                                defaultMessage="Hi, {name}! Welcome to your Teamwork Collection 🎉"
                                                values={{ name }}
                                            />
                                        ) : (
                                            // Control: existing welcome headline
                                            <h3>
                                                <FormattedMessage
                                                    id="WacConfirmationPage.teamworkCollectionProvisioningComplete.name"
                                                    defaultMessage="Hi {name},"
                                                    values={{ name }}
                                                />
                                                <br />
                                                <FormattedMessage
                                                    id="WacConfirmationPage.teamworkCollectionProvisioningComplete.welcome"
                                                    defaultMessage="Welcome to Teamwork Collection"
                                                />
                                            </h3>
                                        )}
                                    </h3>
                                    <p
                                        css={EndAnimationParagraphStyle}
                                        data-testid="teamwork-collection-provisioning-message-complete"
                                    >
                                        {isWelcomeVariant ? (
                                            // Variant 2 ("welcome"): new welcome screen body copy
                                            <FormattedMessage
                                                id="WacConfirmationPage.teamworkCollectionProvisioningComplete.welcome.body"
                                                defaultMessage="Your team just got a serious upgrade! From projects to docs to video updates to AI — everything your team needs is here. Let us show you around."
                                            />
                                        ) : (
                                            // Control: existing body copy
                                            <FormattedMessage
                                                id="WacConfirmationPage.teamworkCollectionProvisioningComplete"
                                                defaultMessage="You've unlocked a smarter way for your team to connect,{br}collaborate and get more done."
                                                values={{ br: <br /> }}
                                            />
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                    </ModalOverlay>
                </Background>
            </DwellTimeWrapper>
        </UfoCustomTimingWrapper>
    );
};
